import {
  useReducer,
  useMemo,
  useEffect,
  useContext,
  FunctionComponent,
  ComponentType,
  Dispatch
} from 'react';
import { createPortal } from 'react-dom';

import { createUID } from '../../utils';
import { useConfiguration, useScrollToggle } from '../../hooks';
import { OmitStrict } from '../../types';

import DockedModals from './DockedModals';
import {
  ModalMethods,
  ModalOptions,
  ModalObject,
  ModalManagerProps,
  ModalState,
  modalInitializedKey,
  managerInitializedKey
} from './Modal.types';
import { ModalContext, ModalManagerContext } from './Contexts';

type ModalAction =
  | 'create'
  | 'update'
  | 'minimize'
  | 'maximize'
  | 'dock'
  | 'activate'
  | 'dismiss'
  | 'unmount';

type ModalReducerPayload<P extends object> =
  | { action: 'create'; modal: ModalObject<P> }
  | {
      action: 'update';
      modal: Partial<OmitStrict<ModalObject<P>, 'props'>> &
        Pick<ModalObject, 'id'> & { props: Partial<ModalObject<P>['props']> };
    }
  | {
      action: Exclude<ModalAction, 'create' | 'update'>;
      modal: Pick<ModalObject, 'id'>;
    };

const reducer = <P extends object>(
  modals: ModalObject[],
  payload: ModalReducerPayload<P>
): ModalObject<any>[] => {
  switch (payload.action) {
    case 'create':
      return [
        ...modals.map(modal => {
          if (
            (!payload.modal.options.alert &&
              payload.modal.state === 'open' &&
              !modal.options.alert &&
              modal.state === 'open' &&
              modal.options.minimizable) ||
            payload.modal.options.dockable
          ) {
            modal.state = 'minimized';
          }

          return modal;
        }),
        payload.modal
      ];

    case 'unmount':
      return modals.filter(modal => modal.id !== payload.modal.id);

    case 'dismiss':
      return modals.map(modal => {
        modal.state = modal.id === payload.modal.id ? 'closed' : modal.state;
        return modal;
      });

    case 'update':
      return modals.map(modal => {
        modal.props =
          modal.id === payload.modal.id ? { ...modal.props, ...payload.modal.props } : modal.props;
        return modal;
      });

    case 'activate':
      return modals.map(modal => {
        if (modal.id === payload.modal.id && !modal.options.dockable) {
          modal.state = 'open';
        } else if (modal.id === payload.modal.id && modal.options.dockable) {
          modal.state = 'docked';
        } else if (!modal.options.alert && modal.state === 'open' && modal.options.minimizable) {
          modal.state = 'minimized';
        }

        return modal;
      });

    case 'minimize':
      return modals.map(modal => {
        modal.state =
          modal.id === payload.modal.id && modal.options.minimizable ? 'minimized' : modal.state;
        return modal;
      });

    case 'maximize':
      return modals.map(modal => {
        modal.state =
          modal.id === payload.modal.id && modal.options.maximizable ? 'maximized' : modal.state;
        return modal;
      });

    case 'dock':
      return modals.map(modal => {
        modal.state =
          modal.id === payload.modal.id && modal.options.dockable ? 'docked' : 'minimized';
        return modal;
      });

    default:
      return modals;
  }
};

const WrappedModal = ({
  Component,
  id,
  options: { alert, dismissible, dockable, minimizable, maximizable },
  state,
  methods,
  props,
  top
}: ModalObject & { top: boolean }) => (
  <ModalContext.Provider
    value={{
      id,
      alert,
      dismissible,
      minimizable,
      maximizable,
      dockable,
      state,
      top,
      [modalInitializedKey]: true,
      ...methods
    }}
  >
    <Component {...props} />
  </ModalContext.Provider>
);

const RenderModals = ({ modals }: { modals: ModalObject[] }) => {
  const { open, minimized, alerts } = modals.reduce<{
    open: ModalObject[];
    minimized: ModalObject[];
    alerts: ModalObject[];
  }>(
    (obj, modal) => {
      if (modal.options.alert) obj.alerts.push(modal);
      else if (modal.state === 'minimized' || modal.state === 'docked') {
        obj.minimized.push(modal);
      } else obj.open.push(modal);

      return obj;
    },
    { open: [], minimized: [], alerts: [] }
  );

  return (
    <>
      {open.map((modal, i) => (
        <WrappedModal
          {...modal}
          top={alerts.length === 0 && i === open.length - 1}
          key={modal.id}
        />
      ))}
      {minimized.length > 0 && (
        <DockedModals>
          {minimized.map(modal => (
            <WrappedModal {...modal} top={false} key={modal.id} />
          ))}
        </DockedModals>
      )}
      {alerts.map((modal, i) => (
        <WrappedModal {...modal} top={i === open.length - 1} key={modal.id} />
      ))}
    </>
  );
};

const ModalManager: FunctionComponent<ModalManagerProps> = ({
  children,
  context = ModalManagerContext
}) => {
  const [modals, dispatch] = useReducer(reducer, []);
  const { disableScroll, enableScroll } = useScrollToggle();

  const providerValue = useMemo(
    () => ({
      create: <P extends object>(
        Component: ComponentType<P>,
        props?: P,
        options?: ModalOptions
      ) => {
        const id = options?.id || createUID();

        const methods: ModalMethods<P> = {
          dismiss: () => {
            dispatch({ action: 'dismiss', modal: { id } });
            options?.onDismiss?.(id);
          },
          update: updateProps => {
            (dispatch as Dispatch<ModalReducerPayload<P>>)({
              action: 'update',
              modal: { id, props: updateProps }
            });
          },
          minimize: () => {
            dispatch({ action: 'minimize', modal: { id } });
            options?.onMinimize?.(id);
          },
          maximize: () => {
            dispatch({ action: 'maximize', modal: { id } });
            options?.onMaximize?.(id);
          },
          dock: () => {
            dispatch({ action: 'dock', modal: { id } });
            options?.onDock?.(id);
          },
          activate: () => {
            dispatch({ action: 'activate', modal: { id } });
            options?.onActivate?.(id);
          },
          unmount: () => {
            dispatch({ action: 'unmount', modal: { id } });
          }
        };

        let defaultState: ModalState;
        if (options?.defaultMinimized) {
          defaultState = 'minimized';
        } else if (options?.dockable) {
          defaultState = 'docked';
        } else {
          defaultState = 'open';
        }

        (dispatch as Dispatch<ModalReducerPayload<P>>)({
          action: 'create',
          modal: {
            id,
            methods,
            Component,
            props,
            state: defaultState,
            options: {
              alert: false,
              minimizable: false,
              maximizable: false,
              dockable: false,
              defaultMinimized: false,
              unmountWhenMinimized: true,
              ...options,
              dismissible: !!(options?.alert ? false : options?.dismissible ?? true)
            }
          }
        });

        return methods;
      },
      ModalContext,
      [managerInitializedKey]: true
    }),
    []
  );

  useEffect(() => {
    if (modals.some(modal => modal.state === 'open' && !modal.options.dockable)) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [modals]);

  const { portalTarget } = useConfiguration();

  const priorCtx = useContext(context);

  const priorCtxValue = useMemo(
    () =>
      context === ModalManagerContext
        ? priorCtx
        : {
            ...priorCtx,
            create: (Comp, props, opts) => {
              const WrappedComp: typeof Comp = compProps => (
                <ModalManager context={context}>
                  <Comp {...compProps} />
                </ModalManager>
              );
              return priorCtx.create(WrappedComp, props, opts);
            }
          },
    [priorCtx, context]
  );

  // Don't create additional Modal portals.
  if (priorCtx[managerInitializedKey])
    return (
      <ModalManagerContext.Provider value={priorCtxValue}>{children}</ModalManagerContext.Provider>
    );

  return (
    <ModalManagerContext.Provider value={providerValue}>
      {children}
      {modals.length > 0 &&
        portalTarget &&
        createPortal(<RenderModals modals={modals} />, portalTarget)}
    </ModalManagerContext.Provider>
  );
};

export default ModalManager;
